import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { UniversalPage, Card, Button } from '@sighthost/ui';
import { Loader2, AlertCircle } from 'lucide-react';
import { salesService } from '@/domains/sales/services/salesService';

/**
 * SalesConvertHandoff — calls `lead-convert` then redirects to the admin
 * Create-Order route returned by the EF. Used when an agent clicks Convert
 * from `/sales/leads/:id`.
 *
 * Auto-runs on mount. If conversion fails, surfaces the error and lets the
 * agent return to the lead detail.
 */

export default function SalesConvertHandoff() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    salesService.convertLead(id)
      .then(({ redirect_url }) => {
        navigate(redirect_url, { replace: true });
      })
      .catch((e: Error) => setError(e.message));
  }, [id, navigate]);

  return (
    <UniversalPage title="Converting lead…">
      {error ? (
        <Card className="p-6">
          <div className="flex items-start gap-3 text-danger">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <h2 className="text-base font-semibold">Conversion failed</h2>
              <p className="mt-1 text-sm">{error}</p>
            </div>
          </div>
          <Button onClick={() => navigate(`/sales/leads/${id}`)} variant="outline" className="mt-4">
            Back to lead
          </Button>
        </Card>
      ) : (
        <Card className="p-8 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
          <p className="mt-3 text-sm text-muted-foreground">Stamping attribution + sending claim email…</p>
        </Card>
      )}
    </UniversalPage>
  );
}
